import * as React from 'react';
import { useSelector } from 'react-redux';

import { PageAlert } from '@app/components/Alerts/PageAlert';
import AlertSectionLayout from '@app/components/AlertSectionLayout';
import { useDisplayDisconnectAlert, useShouldShowClickToDisconnect } from '@app/hooks';
import { Alert, AlertType, selectAlerts, selectDisconnectInternet, selectIsPreinstall } from '@app/redux';
import { checkAlertDismissed } from '@app/utils';

import { useModalContext } from '../../AddDataBoost';

type AlertsSectionProps = {
  navigateToPage: (link: string) => void;
};

export const AlertsSection = ({ navigateToPage }: AlertsSectionProps): JSX.Element | null => {
  const { success: hasDisconnected } = useSelector(selectDisconnectInternet);
  const hasPreinstall = useSelector(selectIsPreinstall);

  const showClickToDisconnect = useShouldShowClickToDisconnect();
  const displayDisconnectAlert = useDisplayDisconnectAlert();

  React.useEffect(() => {
    if (hasDisconnected && showClickToDisconnect && !hasPreinstall) {
      displayDisconnectAlert();
    }
  }, [hasDisconnected]);

  const { alerts, loading } = useSelector(selectAlerts);

  const { dialogTypeHandlers } = useModalContext();
  const { setBuyMoreDialog } = dialogTypeHandlers;

  const [displayAlerts, setDisplayAlerts] = React.useState<Alert[]>([]);

  React.useEffect(() => {
    const alertsToDisplay = alerts.filter((alert) => {
      if (!alert) {
        return false;
      }

      const wasDismissed = checkAlertDismissed(alert.type);
      return !wasDismissed;
    });

    // Sort so that ElectronicCustomerAgreement alert is always first if it exists
    const electronicCustomerAgreementType = AlertType.ElectronicCustomerAgreement;
    const sortedAlerts = [...alertsToDisplay].sort((a, b) => {
      if (a.type === electronicCustomerAgreementType && b.type !== electronicCustomerAgreementType) return -1;
      if (a.type !== electronicCustomerAgreementType && b.type === electronicCustomerAgreementType) return 1;
      return 0;
    });

    setDisplayAlerts(sortedAlerts);
  }, [alerts]);

  if (loading) {
    return null;
  }

  return (
    <AlertSectionLayout>
      {displayAlerts
        .filter((alert) => !checkAlertDismissed(alert.type))
        .map((alert, i) => (
          <PageAlert
            key={i}
            alert={alert}
            loading={loading}
            setBuyMoreDialog={setBuyMoreDialog}
            navigateToPage={navigateToPage}
          />
        ))}
    </AlertSectionLayout>
  );
};

export default AlertsSection;
